import { useEffect, useRef, useState } from "react";
import { useEntryBooking } from "../../../hooks/react-query/useNewService";
import { Checkbox, TextField } from "../../../components/ui";
import { ToggleButton } from "../../../components/ui/Toggle/ToggleButton";
import { useServiceCarStore } from "../../../store/serviceCarStore";
import { Slider } from '../../../components/service/NewServiceBooking/Slider';
import { size } from "lodash";
import { CheckCircleIcon, WarningCircleIcon } from "../../../icons";
import Loader from "../../../components/elements/loader";
import { toast } from "../../../components/ui/Toast/useToast";
import { BookingStepOneTesla } from "./BookingStepOneTesla";
import usePersonStore from "../../../store/personStore";
import useLoyaltyStore from "../../../store/loyaltyStore";
import { AlertBanner } from "../../../components/ui/AlertBanner";

export const BookingStepOne = ({ state, dispatch }) => {
  // Step One: Here the user chooses the types of services
  // he/she wants to book an appointment for.

  // LOCAL STATE:
  const [sliderFirstChange, setSliderFirstChange] = useState(false)
  const [isEntryLoading, setIsEntryLoading] = useState(false)

  // FETCH DATA:
const { mutateAsync: enterBooking } = useEntryBooking();
  
  // LOCAL VARIABLES:
  const { carInfo } = useServiceCarStore();
  const { isLoyaltyEligible } = usePersonStore.getState();
  const { canRedeemPickUp, loyaltyProducts } = useLoyaltyStore.getState();
  const productAwardPoints = loyaltyProducts?.filter((product) => product?.name === "Pick Up Booking")?.[0]?.accrualPoints
  
  const didEnterBookingRef = useRef(false);
  
  // USE EFFECTS:
  useEffect(() => {
    // prevent double call (StrictMode) & unwanted re-fire
    if (didEnterBookingRef.current) return;

    // wait until required data exists
    if (!carInfo?.plateno) return;
    
    didEnterBookingRef.current = true;
    enterBookingHandler({onMount:true});
  }, [carInfo?.plateno]);

  useEffect(()=>{
    dispatch({ 
      type: "SET_OPTION_ELIGIBILITY", 
      option: 'standarizedService', 
      value: (state?.showServiceLightIndicatorNeeded && state?.serviceLightIndicator ) ? state?.apiOptions?.standarizedService?.currentlyAllowed : state?.currentMilleage >= state?.mileageLimits?.standarizedService && state?.apiOptions?.standarizedService?.currentlyAllowed,
    });
    dispatch({ 
      type: "SET_OPTION_ELIGIBILITY", 
      option: 'tyresChange', 
      value:  state?.currentMilleage >= state?.mileageLimits?.tyresChange && state?.apiOptions?.tyreChange?.currentlyAllowed,
    });
    dispatch({ 
      type: "SET_OPTION_ELIGIBILITY", 
      option: 'bodyWork', 
      value:  state?.currentMilleage >= state?.mileageLimits?.bodyWork && state?.apiOptions?.bodyWork?.currentlyAllowed,
    });
  },[state?.mileageLimits, state?.currentMilleage])

  useEffect(()=>{
    if(!state?.optionsEligibility?.standarizedService){
      dispatch({ type: "SET_OPTION", option: 'standarizedService', value: false });
    }
    if(!state?.optionsEligibility?.tyresChange){
      dispatch({ type: "SET_OPTION", option: 'tyresChange', value: false });
    }
    if(!state?.optionsEligibility?.bodyWork){
      dispatch({ type: "SET_OPTION", option: 'bodyWork', value: false });
    }
  }, [state?.optionsEligibility])

  // HANDLERS & FUNCTIONS

  const handleKeyDown = (event) => {
    if (
      !(
        (
          (event.keyCode >= 48 && event.keyCode <= 57) || // Numbers
          (event.keyCode >= 96 && event.keyCode <= 105) || // Numeric keypad
          event.keyCode === 8 || // Backspace
          event.keyCode === 46 || // Delete
          event.keyCode === 37 || // Left arrow
          event.keyCode === 39
        ) // Right arrow
      )
    ) {
      event.preventDefault();
    }
  };

  const handleOptionsToggling = (optionType) => {
    dispatch({ type: "TOGGLE_OPTION", option: optionType });
    if(!state?.hasUsertouchedCheckboxes) {
      dispatch({ type: "SET_USERTOUCHED_CHECKBOXES", hasUsertouchedCheckboxes: true });
    }
  };

  const enterBookingHandler = async ({
    serviceLightIndicator = state.serviceLightIndicator,
    onMount = false,
  } = {}) => {
    setIsEntryLoading(true);

    const entryPayload = {
      mileage: onMount ? 850000 : state.currentMilleage > 0 ? state?.currentMilleage : 1,
      plateno: carInfo?.plateno,
      serviceLightIndicator,
    };

    try {
      const response = await enterBooking({ payload: entryPayload });

      if (response?.exceptions && size(response.exceptions) > 0) {
        toast({
          variant: 'destructive',
          description: 'Παρουσιάστηκε κάποιο πρόβλημα',
        });
      } else {
        dispatch({ type: "SET_STEPSTATUS", stepStatus: response?.name });
        dispatch({
          type: "SET_API_OPTIONS",
          value: response.inputs.workSelection.options,
        });

        dispatch({
          type: 'SET_SERVICEFLOWID',
          serviceFlowId: response?.id,
        });

        dispatch({
          type: 'SET_AUTHORIZEDEV',
          isAuthorizedEV: response?.['Authorized/EV'],
        });

        setSliderFirstChange(true);

        dispatch({
          type: 'SET_MILEAGE_LIMITS',
          value: {
            standarizedService:
              response?.inputs?.workSelection?.options?.standarizedService?.mileage,
            tyresChange:
              response?.inputs?.workSelection?.options?.tyreChange?.mileage,
            bodyWork:
              response?.inputs?.workSelection?.options?.bodyWork?.mileage,
          },
        });

        dispatch({
          type: "COMPUTE_OPTIONS_STATUS",
          payload: {
            apiOptions: response.inputs.workSelection.options,
            currentMileage: state.currentMilleage,
          },
        });
      }
    } catch (e) {
      setIsEntryLoading(false);
      toast({
        variant: 'destructive',
        description: 'Παρουσιάστηκε κάποιο πρόβλημα',
      });
    } finally {
      setIsEntryLoading(false);
    }
  };


  return (
    <>
      {isEntryLoading && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/20 backdrop-blur-sm">
          <Loader />
        </div>
      )}
      {state?.isTesla ? 
        <BookingStepOneTesla state={state} dispatch={dispatch} />
        : <> 
          <div className="flex flex-row gap-6 w-full justify-center">
            <div className="flex flex-col gap-10 max-sm:w-full">
              <div className="flex flex-col gap-6 max-sm:max-w-fit">
                <h4 >Πόσα χιλιόμετρα έχει διανύσει το αυτοκίνητο;</h4>
                <div className="flex flex-row justify-between items-center">
                  <h5 className="text-body-large">
                    Χιλιόμετρα (υποχρεωτικό)
                  </h5>
                  <TextField
                    autoComplete="off"
                    onChange={(e) => {
                      dispatch({
                        type: "SET_CURRENT_MILLEAGE",
                        currentMilleage: e.target.value,
                      });
                      !sliderFirstChange && !isEntryLoading && enterBookingHandler()
                    }}
                    min={0}
                    onKeyDown={handleKeyDown}
                    type={"number"}
                    value={state?.currentMilleage}
                    className={" w-[124px]"}
                    prefix="km"
                  />
                </div>
              </div>
              <div className="w-full">
                <Slider
                  value={[state?.currentMilleage]}
                  onValueChange={(value) => {
                      dispatch({
                        type: "SET_CURRENT_MILLEAGE",
                        currentMilleage: value[0],
                      });
                    !sliderFirstChange && !isEntryLoading && enterBookingHandler()
                  }}
                  defaultValue={[15000]}
                  min={0}
                  max={300000}
                  dotStep={25000}
                />
              </div>
        
              {state?.showServiceLightIndicatorNeeded && <div>
                <h4 className='pb-6'>Βλέπεις τη φωτεινή ένδειξη για service στο καντράν;</h4>
                <div className='flex flex-row gap-4'>
                  <ToggleButton
                    className='data-[state=checked]:bg-[#13EF00]'
                    checked={state?.serviceLightIndicator} 
                    onCheckedChange={async (value)=>{
                      await dispatch({
                        type: "SET_SERVICE_LIGHT_INDICATOR",
                        serviceLightIndicator: value,
                      });
                      enterBookingHandler({
                        serviceLightIndicator: value,
                        onMount:true
                      });
                    }
                  } />
                    <p className=''>Η φωτεινή ένδειξη είναι αναμμένη</p>
                </div>
              </div>}
        
              <div>
                <h4 className=''>Επίλεξε τύπο εργασίας:</h4>
                <div className="pt-6 flex flex-row max-sm:flex-col max-sm:gap-1 justify-start gap-4">
                  <div className='flex flex-col gap-3 h-[108px] w-[162px] max-sm:w-full'>
                    <div
                      className="w-full bg-white rounded-[12px] p-4 flex lg:flex-col lg:justify-between lg:h-[140px]"
                      style={{
                        boxShadow: "0px 2px 6px 0px #63636333",
                      }}
                    >
                      <h6 className={`${!state?.optionsEligibility?.standarizedService && "text-fadedgrey"} break-all max-sm:break-normal max-sm:w-full`}>Τακτικό service</h6>
                      <div className={`w-full flex ${state?.optionsEligibility?.standarizedService && state?.options?.standarizedService ? "justify-end" : "justify-between max-sm:justify-end"}`}>
                        <>
                          {!state?.optionsEligibility?.standarizedService ? 
                            <WarningCircleIcon color="#FF922E" width="24" height="24" />
                            : !state?.options?.standarizedService && <CheckCircleIcon  width='20' height='20' className={'stroke-[#2E9BFF] max-sm:hidden'} />
                          }
                          {state?.optionsEligibility?.standarizedService && <Checkbox
                            checked={state.options.standarizedService}
                            onCheckedChange={() => {
                              handleOptionsToggling("standarizedService");
                            }}
                            className='h-6 w-6'
                            />}
                        </>
                      </div>
                    </div>
                  </div>
        
                  <div className='flex flex-col gap-3 h-[108px] w-[162px] max-sm:w-full'>
                    <div
                      className="w-full bg-white rounded-[12px] p-4 flex lg:flex-col lg:justify-between lg:h-[140px]"
                      style={{
                        boxShadow: "0px 2px 6px 0px #63636333",
                      }}
                    >
                      <h6 className={`${!state?.optionsEligibility?.tyresChange && "text-fadedgrey"} break-normal max-sm:w-full `}>Αντικατάσταση ελαστικών</h6>
                      <div className={`w-full flex ${state?.optionsEligibility?.tyresChange && state?.options?.tyresChange ? "justify-end" : "justify-between max-sm:justify-end"}`}>
                        <>
                          {!state?.optionsEligibility?.tyresChange ? 
                            <WarningCircleIcon color="#FF922E" width="24" height="24" />
                            : !state?.options?.tyresChange && <CheckCircleIcon  width='20' height='20' className={'stroke-[#2E9BFF] max-sm:hidden'} />
                          }
                            {state?.optionsEligibility?.tyresChange && <Checkbox
                              checked={state.options.tyresChange}
                              onCheckedChange={() => {
                                handleOptionsToggling("tyresChange");
                              }}
                              className='h-6 w-6'
                              />}
                          </>
                      </div>
                    </div>
                  </div>
        
                  <div className='flex flex-col gap-3 h-[108px] w-[162px] max-sm:w-full'>
                    <div
                      className="w-full bg-white rounded-[12px] p-4 flex lg:flex-col lg:justify-between lg:h-[140px]"
                      style={{
                        boxShadow: "0px 2px 6px 0px #63636333",
                      }}
                    >
                      <h6 className={`${!state?.optionsEligibility?.bodyWork && "text-fadedgrey"} break-normal max-sm:w-full`} >Εργασίες φανοποιίας</h6>
                      <div className={`w-full flex ${state?.optionsEligibility?.bodyWork && state?.options?.bodyWork ? "justify-end" : "justify-between max-sm:justify-end"}`}>
                        <>
                          {!state?.optionsEligibility?.bodyWork ? 
                            <WarningCircleIcon color="#FF922E" width="24" height="24" />
                            : !state?.options?.bodyWork && <CheckCircleIcon  width='20' height='20' className={'stroke-[#2E9BFF] max-sm:hidden'} />
                          }
                            {state?.optionsEligibility?.bodyWork && <Checkbox
                              checked={state.options.bodyWork}
                              onCheckedChange={() => {
                                handleOptionsToggling("bodyWork");
                              }}
                              className='h-6 w-6'
                            />}
                          </>
                      </div>
                    </div>
                  </div>  
                </div>
              </div>
              {/* {isLoyaltyEligible && !!productAwardPoints && productAwardPoints > 0 &&  */}
              {isLoyaltyEligible &&
              <AlertBanner severity="info" className='pb-1'>
                  <div className='flex flex-row gap-[14px] justify-start align-top p-3'>
                      <CheckCircleIcon width='20' height='20' className={'stroke-[#2E9BFF] shrink-0'} />
                      <p className='text-sm font-semibold'>Κέρδισε χιλιόμετρα επιβράβευσης στο Avis More με την ολοκλήρωση του ραντεβού σου. ✨</p>
                  </div>
              </AlertBanner>
              }
            </div>
          </div>
        </>

      }
    </>
  );
};
